import { X } from 'lucide-react';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import SavingsModalRow from './SavingsModalRow';
import { appAxios } from 'api/axios';
import { sendCatchFeedback, sendFeedback } from 'functions/feedback';
import Button from '../button';
import {
  formatDate,
  formatDuration,
  formatToNaira,
} from 'functions/currencyConveter';
import SkeletonRow from './SkeletonLoader';

interface SingleGoalData {
  goalName: string;
  targetAmount: number;
  preferedSavingAmount: number;
  startDate: string;
  endDate: string;
  nextSavingDate: string;
  autoSave: string;
  status: string;
  duration: number;
  savingsFrequency: string;
  savingType: string;
  interest: number;
  savingMethod: string;
  currentSavedAmount: number;
  autoWithDraw: boolean;
}
interface SavingsModalProps {
  onClose: () => void;
  handleReinvest: () => void;
  setIsWithdrawPopupOpen: Dispatch<SetStateAction<boolean>>;
  id?: string;
}
const SavingsModal = ({
  onClose,
  setIsWithdrawPopupOpen,
  handleReinvest,
  id,
}: SavingsModalProps) => {
  const [singleGoal, setSingleGoal] = useState<SingleGoalData>(
    {} as SingleGoalData
  );
  const [loading, setLoading] = useState(false);
  const fetchSingleSavingsData = async (goalId: string) => {
    setLoading(true);
    try {
      const response = await appAxios.get(
        `get-single-customer-savings?goalId=${goalId}`
      );

      if (response.data.success) {
        const goalData = response.data.data;
        setSingleGoal(goalData);
      } else {
        sendFeedback(response.data.message);
      }
    } catch (error) {
      sendCatchFeedback(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!id) return;
    fetchSingleSavingsData(id);
  }, []);

  const isRolledOver = useMemo(() => {
    return singleGoal.status === 'RolledOver';
  }, [singleGoal.status]);
  const showWithdraw = () => {
    if (singleGoal.currentSavedAmount > 0) {
      setIsWithdrawPopupOpen(true);
      onClose();
    } else {
      sendFeedback(
        'You cannot withdraw because there are no savings available.',
        'error'
      );
    }
  };
  const primaryAction = () => {
    if (singleGoal.status === 'RolledOver') {
      handleReinvest();
    } else {
    }
    showWithdraw();
  };
  return (
    <div className="flex overflow-y-auto fixed inset-0 z-[9999] justify-center items-center px-5 w-full  bg-gray-500 bg-opacity-50">
      <div className="relative h-[90%] overflow-auto p-6 w-full max-w-md bg-white rounded-lg shadow-lg">
        <button
          type="button"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-900"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        <h2 className="text-lg font-semibold text-gray-800">
          {singleGoal.goalName}
        </h2>
        <div className="p-4 mt-4 text-center bg-gray-50 rounded-md">
          {loading ? (
            <>
              <div className="mx-auto h-8 w-1/2 rounded bg-gray-200" />
              <div className="mx-auto mt-3 h-5 w-24 rounded bg-gray-200" />
            </>
          ) : (
            <>
              <p className="text-3xl font-bold text-gray-900">
                {formatToNaira(singleGoal.currentSavedAmount)}
              </p>
              <span
                className={`inline-block px-3 py-1 mt-2 text-sm rounded-lg ${
                  isRolledOver ||
                  singleGoal.status === 'Active' ||
                  singleGoal.status === 'Completed'
                    ? 'text-green-600 bg-green-100'
                    : 'text-red-600 bg-red-100'
                }`}
              >
                {isRolledOver ? 'Successful' : singleGoal.status}
              </span>
            </>
          )}
        </div>
        {loading ? (
          <div className="mt-6 space-y-4">
            {Array.from({ length: 14 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <SavingsModalRow
              label="Savings Title"
              value={singleGoal.goalName}
            />
            <SavingsModalRow
              label="Savings Goal"
              value={formatToNaira(singleGoal.targetAmount)}
            />
            <SavingsModalRow
              label="Start Date"
              value={formatDate(singleGoal.startDate)}
            />
            <SavingsModalRow
              label="Maturity Date"
              value={formatDate(singleGoal.endDate)}
            />
            <SavingsModalRow
              label="Duration"
              value={formatDuration(singleGoal.startDate, singleGoal.endDate)}
            />
            <SavingsModalRow
              label="Saved Amount"
              value={formatToNaira(singleGoal.currentSavedAmount)}
            />
            <SavingsModalRow
              label="Savings Frequency"
              value={singleGoal.savingsFrequency}
            />
            <SavingsModalRow
              label="Saving Type"
              value={singleGoal.savingType}
            />
            <SavingsModalRow
              label="Next Saving Date"
              value={singleGoal.nextSavingDate}
            />
            <SavingsModalRow
              label="Preferred Saving Amount"
              value={formatToNaira(singleGoal.preferedSavingAmount)}
            />
            <SavingsModalRow
              label="Saving Method"
              value={singleGoal.savingMethod}
            />
            <SavingsModalRow
              label="Auto Withdraw"
              value={singleGoal.autoWithDraw ? 'Yes' : 'No'}
            />
            <SavingsModalRow
              label="Auto Save"
              value={singleGoal.autoSave ? 'Yes' : 'No'}
            />
            <SavingsModalRow
              label="Interest"
              value={formatToNaira(singleGoal.interest)}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mt-6">
          {singleGoal.status !== 'Completed' ? (
            <>
              <Button
                onClick={() => onClose()}
                className="w-full text-yellow-500 bg-white border border-yellow-500 hover:bg-yellow-50"
              >
                {isRolledOver ? 'Withdraw' : 'Cancel'}
              </Button>
              <Button
                onClick={primaryAction}
                className="w-full text-white bg-yellow-500 hover:bg-yellow-600"
              >
                {isRolledOver ? 'Reinvest' : 'Withdraw'}
              </Button>
            </>
          ) : (
            <Button
              onClick={onClose}
              className="col-span-2 w-full text-white bg-yellow-500 hover:bg-yellow-600"
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavingsModal;
